import { FinancialAuditService } from "@/services/financial-audit.service";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";

export interface FinancialAuditSummary {
  netBalance: number;
  revenue: number;
  deductions: number;
}

export interface FinancialAuditCalculation {
  totalBilled: number;
  refunds: number;
  chargebackFees: number;
  processingFees: number;
  withdrawalFees: number;
  calculatedNetBalance: number;
}

export interface FinancialAuditDistribution {
  label: string;
  value: number;
  percentage: number;
}

export interface FinancialAuditAudit {
  calculatedBalance: number;
  registeredBalance: number;
  difference: number;
  hasDifference: boolean;
}

export interface FinancialAuditData {
  summary: FinancialAuditSummary;
  calculation: FinancialAuditCalculation;
  distribution: FinancialAuditDistribution[];
  audit: FinancialAuditAudit;
}

interface UseFinancialAuditParams {
  dateRange: DateRange | undefined;
  enabled?: boolean;
  currency?: string;
}

export function useFinancialAudit({
  dateRange,
  enabled = true,
  currency = 'BRL',
}: UseFinancialAuditParams) {
  const startDate = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : "";
  const endDate = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : "";

  const query = useQuery<FinancialAuditData>({
    queryKey: ["financial-audit", startDate, endDate, currency],
    queryFn: async () => {
      const response = await FinancialAuditService.getFinancialAudit({
        startDate,
        endDate,
        currency,
      });

      return response;
    },
    enabled: enabled && !!startDate && !!endDate,
    staleTime: 1000 * 60 * 5,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
